/**
 * Filesystem Storage Driver
 *
 * Almacena archivos en el sistema de archivos local.
 */

import { createReadStream, createWriteStream, existsSync, unlinkSync, mkdirSync } from 'node:fs'
import { readFile, readdir, stat, copyFile, rename, mkdir } from 'node:fs/promises'
import { join, dirname, extname, basename } from 'node:path'
import { createHash } from 'node:crypto'
import type { StorageDriver, StorageFile, StorageFileInfo, PutOptions, GetOptions } from './driver.interface.js'
import type { StorageConfig } from '../storage.config.js'

export class FilesystemDriver implements StorageDriver {
  private basePath: string
  private baseUrl: string | undefined
  private generateId: () => string

  constructor(config: StorageConfig) {
    this.basePath = config.basePath
    this.baseUrl = config.baseUrl
    this.generateId = config.generateId

    // Crear directorio base si no existe
    if (!existsSync(this.basePath)) {
      mkdirSync(this.basePath, { recursive: true })
    }
  }

  async put(buffer: Buffer, filename: string, options?: PutOptions): Promise<StorageFile> {
    const id = this.generateId()
    const ext = extname(filename) || ''
    const diskFilename = `${id}${ext}`
    const folder = options?.folder || ''
    const relativePath = folder ? `${folder}/${diskFilename}` : diskFilename
    const fullPath = join(this.basePath, relativePath)

    // Crear subdirectorio si es necesario
    const dir = dirname(fullPath)
    if (!existsSync(dir)) {
      mkdirSync(dir, { recursive: true })
    }

    // Calcular hash SHA256
    const hash = createHash('sha256').update(buffer).digest('hex')

    // Escribir archivo
    await new Promise<void>((resolve, reject) => {
      const stream = createWriteStream(fullPath)
      stream.on('finish', resolve)
      stream.on('error', reject)
      stream.write(buffer)
      stream.end()
    })

    return {
      id,
      filename,
      diskFilename,
      mimetype: options?.mimetype || 'application/octet-stream',
      size: buffer.length,
      folder: folder || undefined,
      path: relativePath,
      url: this.getUrl(relativePath) || undefined,
      hash
    }
  }

  async get(path: string, options?: GetOptions): Promise<NodeJS.ReadableStream> {
    const fullPath = join(this.basePath, path)
    if (!existsSync(fullPath)) {
      throw new Error(`File not found: ${path}`)
    }
    const streamOpts: { start?: number; end?: number } = {}
    if (options?.start !== undefined) streamOpts.start = options.start
    if (options?.end !== undefined) streamOpts.end = options.end
    return createReadStream(fullPath, streamOpts)
  }

  async getBuffer(path: string): Promise<Buffer> {
    const fullPath = join(this.basePath, path)
    return readFile(fullPath)
  }

  async delete(path: string): Promise<void> {
    const fullPath = join(this.basePath, path)
    if (existsSync(fullPath)) {
      unlinkSync(fullPath)
    }
  }

  async exists(path: string): Promise<boolean> {
    const fullPath = join(this.basePath, path)
    return existsSync(fullPath)
  }

  getUrl(path: string): string | null {
    if (!this.baseUrl) return null
    return `${this.baseUrl}/files/${path}`
  }

  // ============================================================================
  // EXTENDED METHODS
  // ============================================================================

  async list(folder?: string): Promise<StorageFileInfo[]> {
    const targetPath = folder ? join(this.basePath, folder) : this.basePath

    if (!existsSync(targetPath)) {
      return []
    }

    const entries = await readdir(targetPath, { withFileTypes: true })
    const results: StorageFileInfo[] = []

    for (const entry of entries) {
      const entryPath = join(targetPath, entry.name)
      const relativePath = folder ? `${folder}/${entry.name}` : entry.name
      const stats = await stat(entryPath)

      results.push({
        path: relativePath,
        filename: entry.name,
        size: stats.size,
        lastModified: stats.mtime,
        isDirectory: entry.isDirectory()
      })
    }

    return results
  }

  async copy(src: string, dst: string): Promise<StorageFile> {
    const srcPath = join(this.basePath, src)
    const dstPath = join(this.basePath, dst)

    // Crear directorio destino si no existe
    const dstDir = dirname(dstPath)
    if (!existsSync(dstDir)) {
      await mkdir(dstDir, { recursive: true })
    }

    // Copiar archivo
    await copyFile(srcPath, dstPath)

    // Leer metadata del archivo copiado
    const buffer = await readFile(dstPath)
    const stats = await stat(dstPath)
    const hash = createHash('sha256').update(buffer).digest('hex')

    const id = this.generateId()
    const filename = basename(dst)
    const folder = dirname(dst) === '.' ? undefined : dirname(dst)

    return {
      id,
      filename,
      diskFilename: filename,
      mimetype: 'application/octet-stream',
      size: stats.size,
      folder,
      path: dst,
      url: this.getUrl(dst) || undefined,
      hash
    }
  }

  async move(src: string, dst: string): Promise<StorageFile> {
    const srcPath = join(this.basePath, src)
    const dstPath = join(this.basePath, dst)

    // Crear directorio destino si no existe
    const dstDir = dirname(dstPath)
    if (!existsSync(dstDir)) {
      await mkdir(dstDir, { recursive: true })
    }

    // Mover archivo (atómico en mismo filesystem)
    await rename(srcPath, dstPath)

    // Leer metadata del archivo movido
    const buffer = await readFile(dstPath)
    const stats = await stat(dstPath)
    const hash = createHash('sha256').update(buffer).digest('hex')

    const id = this.generateId()
    const filename = basename(dst)
    const folder = dirname(dst) === '.' ? undefined : dirname(dst)

    return {
      id,
      filename,
      diskFilename: filename,
      mimetype: 'application/octet-stream',
      size: stats.size,
      folder,
      path: dst,
      url: this.getUrl(dst) || undefined,
      hash
    }
  }

  async getMetadata(path: string): Promise<StorageFileInfo> {
    const fullPath = join(this.basePath, path)
    const stats = await stat(fullPath)

    return {
      path,
      filename: basename(path),
      size: stats.size,
      lastModified: stats.mtime,
      isDirectory: stats.isDirectory()
    }
  }

  async getSignedUrl(_path: string, _expiresInSeconds: number): Promise<string> {
    // Filesystem no soporta URLs firmadas
    throw new Error('Signed URLs are not supported by the filesystem driver')
  }

  async createFolder(path: string): Promise<void> {
    const fullPath = join(this.basePath, path)
    if (!existsSync(fullPath)) {
      await mkdir(fullPath, { recursive: true })
    }
  }

  getAbsolutePath(path: string): string | null {
    const fullPath = join(this.basePath, path)
    return existsSync(fullPath) ? fullPath : null
  }
}
